import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

export default function StoreSettings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'analytics'
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d'); // '7d', '30d', '90d'

  // Form state for editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'tshirt',
    tags: '',
    variants: [{
      name: 'Default',
      price: 0,
      basePrice: 0,
      finalPrice: 0,
      stock: 100,
      color: '#000000',
      colorName: 'Custom',
      size: 'M',
      material: 'cotton'
    }],
    printSettings: {
      placement: 'front',
      printArea: {
        unit: 'cm',
        width: 20,
        height: 20
      },
      printMethod: 'DTG'
    },
    allowCustomization: true,
    lowStockThreshold: 10,
    images: [],
    designImage: null,
    designFile: null,
    newImages: [],
    deletedImages: []
  });

  // Fetch user, store and products
  useEffect(() => {
    const fetchUserAndProducts = async () => {
      try {
        setLoading(true);
        
        // Get current user info
        const userRes = await api.get('/auth/me');
        const currentUser = userRes.data.data.user;
        
        // Check if user is a store owner
        if (currentUser.role !== 'store_owner' || !currentUser.storeId) {
          setError('You are not authorized to manage a store');
          setLoading(false);
          return;
        }

        // Set store from user data
        setStore(currentUser.storeId);

        // Fetch products for this store
        const productsRes = await api.get(`/products?store=${currentUser.storeId._id}`);
        setProducts(productsRes.data.data.products);

        // Fetch analytics data
        fetchAnalytics(currentUser.storeId._id);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Failed to load store information');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndProducts();
  }, []);

  // Fetch analytics data
  const fetchAnalytics = async (storeId) => {
    try {
      setLoading(true);
      const res = await api.get(`/stores/${storeId}/analytics?period=${timeRange}`);
      setAnalyticsData(res.data.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    if (store) {
      fetchAnalytics(store._id);
    }
  };

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Process data for charts
  const processChartData = (data) => {
    if (!data) return [];
    
    // Process for daily/weekly/monthly trends
    const timeData = [];
    const now = new Date();
    let daysToShow = 30; // Default for 30d
    
    if (timeRange === '7d') daysToShow = 7;
    else if (timeRange === '90d') daysToShow = 90;
    
    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      
      timeData.push({
        date: formattedDate,
        orders: Math.floor(Math.random() * 10), // Replace with actual data
        revenue: Math.floor(Math.random() * 5000) // Replace with actual data
      });
    }
    
    return timeData;
  };

  // Handle product edit
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setIsEditing(true);
    
    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      tags: product.tags?.join(', ') || '',
      variants: product.variants?.map(variant => ({
        name: variant.name || 'Default',
        price: variant.finalPrice || 0,
        basePrice: variant.basePrice || 0,
        finalPrice: variant.finalPrice || 0,
        stock: variant.stock || 100,
        color: variant.colors?.[0]?.hex || '#000000',
        colorName: variant.colors?.[0]?.name || 'Custom',
        size: variant.sizes?.[0]?.name || 'M',
        material: variant.material || 'cotton'
      })) || [{
        name: 'Default',
        price: 0,
        basePrice: 0,
        finalPrice: 0,
        stock: 100,
        color: '#000000',
        colorName: 'Custom',
        size: 'M',
        material: 'cotton'
      }],
      printSettings: product.printSettings || {
        placement: 'front',
        printArea: {
          unit: 'cm',
          width: 20,
          height: 20
        },
        printMethod: 'DTG'
      },
      allowCustomization: product.allowCustomization !== false,
      lowStockThreshold: product.inventory?.lowStockThreshold || 10,
      images: product.images || [],
      designImage: product.design?.image || null,
      designFile: null,
      newImages: [],
      deletedImages: []
    });
  };

  // Handle form changes for editing
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle variant changes for editing
  const handleVariantChange = (index, e) => {
    const newVariants = [...formData.variants];
    newVariants[index][e.target.name] = e.target.value;
    
    if (e.target.name === 'price') {
      newVariants[index].finalPrice = parseFloat(e.target.value);
    }
    
    setFormData({
      ...formData,
      variants: newVariants
    });
  };

  // Add new variant
  const handleAddVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        {
          name: 'New Variant',
          price: 0,
          basePrice: 0,
          finalPrice: 0,
          stock: 100,
          color: '#000000',
          size: 'M',
          material: 'cotton'
        }
      ]
    });
  };

  // Remove variant
  const handleRemoveVariant = (index) => {
    const newVariants = [...formData.variants];
    newVariants.splice(index, 1);
    setFormData({
      ...formData,
      variants: newVariants
    });
  };

  // Submit updated product
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      
      // Add simple fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('tags', formData.tags);
      formDataToSend.append('allowCustomization', formData.allowCustomization);
      formDataToSend.append('lowStockThreshold', formData.lowStockThreshold);
      
      // Add design file if new one was selected
      if (formData.designFile) {
        formDataToSend.append('design', formData.designFile);
      } else if (formData.designImage) {
        formDataToSend.append('designImage', formData.designImage);
      }
      
      // Add new images
      formData.newImages.forEach((file, index) => {
        formDataToSend.append(`images`, file);
      });
      
      // Add deleted images
      if (formData.deletedImages.length > 0) {
        formDataToSend.append('deletedImages', JSON.stringify(formData.deletedImages));
      }
      
      // Format variants according to schema
      const formattedVariants = formData.variants.map(v => ({
        name: v.name || 'Default',
        material: v.material || 'cotton',
        colors: [{ 
          name: v.colorName || 'Custom', 
          hex: v.color || '#000000' 
        }],
        sizes: [{ 
          name: v.size || 'M' 
        }],
        basePrice: parseFloat(v.basePrice) || 0,
        finalPrice: parseFloat(v.finalPrice) || parseFloat(v.price) || 0,
        stock: parseInt(v.stock) || 0
      }));
      
      formDataToSend.append('variants', JSON.stringify(formattedVariants));
      
      // Format print settings
      const formattedPrintSettings = {
        placement: formData.printSettings.placement || 'front',
        printArea: formData.printSettings.printArea || {
          unit: 'cm',
          width: 20,
          height: 20
        },
        printMethod: formData.printSettings.printMethod || 'DTG'
      };
      
      formDataToSend.append('printSettings', JSON.stringify(formattedPrintSettings));

      const { data } = await api.put(`/products/${editingProduct._id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (data.success) {
        setProducts(products.map(p => 
          p._id === editingProduct._id ? data.data.product : p
        ));
        setIsEditing(false);
        setEditingProduct(null);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to update product');
    } finally {
      setLoading(false);
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      setLoading(true);
      const { data } = await api.delete(`/products/${productId}`);
      
      if (data.success) {
        setProducts(products.filter(p => p._id !== productId));
      }
    } catch (err) {
      console.error(err);
      setError('Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  // Create new product
  const handleCreateProduct = async () => {
    navigate(`/store-owner/${user?.storeId?.slug}/products/new`);
  };

  // Handle design image change
  const handleDesignImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        designFile: file,
        designImage: imageUrl
      });
    }
  };

  // Handle additional images change
  const handleAdditionalImagesChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newImages = files.map(file => {
        return {
          file,
          preview: URL.createObjectURL(file)
        };
      });
      
      setFormData({
        ...formData,
        newImages: [...formData.newImages, ...files]
      });
    }
  };

  // Handle image deletion
  const handleDeleteImage = (imageId, newImageIndex = null) => {
    if (imageId) {
      // Existing image from server
      setFormData({
        ...formData,
        deletedImages: [...formData.deletedImages, imageId],
        images: formData.images.filter(img => img._id !== imageId)
      });
    } else if (newImageIndex !== null) {
      // New image not yet uploaded
      setFormData({
        ...formData,
        newImages: formData.newImages.filter((_, index) => index !== newImageIndex)
      });
    }
  };

  if (loading && !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center text-red-600 font-semibold text-xl">
          {error}
        </div>
      </div>
    );
  }

  // Analytics Dashboard Component
  const AnalyticsDashboard = () => (
    <div className="space-y-8">
      {/* Time Range Selector */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => handleTimeRangeChange('7d')}
          className={`px-3 py-1 rounded-md ${timeRange === '7d' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Last 7 Days
        </button>
        <button
          onClick={() => handleTimeRangeChange('30d')}
          className={`px-3 py-1 rounded-md ${timeRange === '30d' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Last 30 Days
        </button>
        <button
          onClick={() => handleTimeRangeChange('90d')}
          className={`px-3 py-1 rounded-md ${timeRange === '90d' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Last 90 Days
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-gray-500 text-sm font-medium">Total Revenue</h3>
          <p className="text-2xl font-bold mt-1">₹{analyticsData?.totalRevenue?.toLocaleString() || '0'}</p>
          <p className="text-green-600 text-sm mt-1">+12% from last period</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-gray-500 text-sm font-medium">Total Orders</h3>
          <p className="text-2xl font-bold mt-1">{analyticsData?.totalOrders?.toLocaleString() || '0'}</p>
          <p className="text-green-600 text-sm mt-1">+8% from last period</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-gray-500 text-sm font-medium">Avg. Order Value</h3>
          <p className="text-2xl font-bold mt-1">₹{analyticsData?.averageOrderValue?.toFixed(2) || '0'}</p>
          <p className="text-green-600 text-sm mt-1">+5% from last period</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-gray-500 text-sm font-medium">Completed Orders</h3>
          <p className="text-2xl font-bold mt-1">{analyticsData?.completedOrders?.toLocaleString() || '0'}</p>
          <p className="text-green-600 text-sm mt-1">{Math.round((analyticsData?.completedOrders / analyticsData?.totalOrders) * 100) || '0'}% success rate</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Orders Trend */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Revenue & Orders Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={processChartData(analyticsData)}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#8884d8" fill="#8884d8" name="Revenue (₹)" />
                <Area type="monotone" dataKey="orders" stroke="#82ca9d" fill="#82ca9d" name="Orders" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Top Selling Products</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={analyticsData?.topProducts?.map(p => ({
                  name: p._id?.name?.substring(0, 15) + (p._id?.name?.length > 15 ? '...' : ''),
                  sales: p.totalSold,
                  revenue: p.totalRevenue
                })) || []}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sales" fill="#8884d8" name="Units Sold" />
                <Bar dataKey="revenue" fill="#82ca9d" name="Revenue (₹)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Order Status Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Completed', value: analyticsData?.completedOrders || 0 },
                    { name: 'Pending', value: analyticsData?.pendingOrders || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  <Cell fill="#0088FE" />
                  <Cell fill="#FF8042" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sales by Category */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Sales by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: 'T-Shirts', sales: 45 },
                  { name: 'Hoodies', sales: 30 },
                  { name: 'Tank Tops', sales: 15 },
                  { name: 'Other', sales: 10 }
                ]}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sales" fill="#8884d8" name="Sales (%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[
            { id: 1, type: 'order', message: 'New order #12345 received', time: '2 hours ago' },
            { id: 2, type: 'product', message: 'Product "Summer T-Shirt" was updated', time: '5 hours ago' },
            { id: 3, type: 'review', message: 'New 5-star review received', time: '1 day ago' },
            { id: 4, type: 'order', message: 'Order #12344 was shipped', time: '2 days ago' },
            { id: 5, type: 'product', message: 'New product "Graphic Hoodie" added', time: '3 days ago' }
          ].map(activity => (
            <div key={activity.id} className="flex items-start pb-2 border-b border-gray-100 last:border-0">
              <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                activity.type === 'order' ? 'bg-blue-500' : 
                activity.type === 'product' ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <div>
                <p className="text-sm">{activity.message}</p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Store Dashboard - {store?.name}</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your store products and view analytics
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('products')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' ? (
          <AnalyticsDashboard />
        ) : isEditing ? (
          // Edit Product Form
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Edit Product</h2>
            
            <form onSubmit={handleUpdateProduct}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Product Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                    required
                  >
                    <option value="tshirt">T-Shirts</option>
                    <option value="hoodie">Hoodies</option>
                    <option value="tank-top">Tank Top</option>
                    <option value="other">Others</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    id="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-900">Variants</h3>
                    <button
                      type="button"
                      onClick={handleAddVariant}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                    >
                      Add Variant
                    </button>
                  </div>
                  <div className="space-y-4">
                    {formData.variants.map((variant, index) => (
                      <div key={index} className="border border-gray-200 rounded-md p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor={`variant-name-${index}`} className="block text-sm font-medium text-gray-700">
                              Variant Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              id={`variant-name-${index}`}
                              value={variant.name}
                              onChange={(e) => handleVariantChange(index, e)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label htmlFor={`variant-price-${index}`} className="block text-sm font-medium text-gray-700">
                              Price (₹)
                            </label>
                            <input
                              type="number"
                              name="price"
                              id={`variant-price-${index}`}
                              value={variant.price}
                              onChange={(e) => handleVariantChange(index, e)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                              min="0"
                              step="0.01"
                              required
                            />
                          </div>

                          <div>
                            <label htmlFor={`variant-material-${index}`} className="block text-sm font-medium text-gray-700">
                              Material
                            </label>
                            <input
                              type="text"
                              name="material"
                              id={`variant-material-${index}`}
                              value={variant.material}
                              onChange={(e) => handleVariantChange(index, e)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                              required
                            />
                          </div>

                          <div>
                            <label htmlFor={`variant-stock-${index}`} className="block text-sm font-medium text-gray-700">
                              Stock
                            </label>
                            <input
                              type="number"
                              name="stock"
                              id={`variant-stock-${index}`}
                              value={variant.stock}
                              onChange={(e) => handleVariantChange(index, e)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                              min="0"
                              required
                            />
                          </div>
                        </div>
                        {/* Add this inside your variant mapping in the edit form */}
                        <div>
                        <label htmlFor={`variant-color-${index}`} className="block text-sm font-medium text-gray-700">
                            Color
                        </label>
                        <input
                            type="color"
                            name="color"
                            id={`variant-color-${index}`}
                            value={variant.color}
                            onChange={(e) => handleVariantChange(index, e)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-1 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm h-10"
                        />
                        </div>

                        <div>
                        <label htmlFor={`variant-size-${index}`} className="block text-sm font-medium text-gray-700">
                            Size
                        </label>
                        <select
                            name="size"
                            id={`variant-size-${index}`}
                            value={variant.size}
                            onChange={(e) => handleVariantChange(index, e)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                            required
                        >
                            <option value="XS">XS</option>
                            <option value="S">S</option>
                            <option value="M">M</option>
                            <option value="L">L</option>
                            <option value="XL">XL</option>
                            <option value="XXL">XXL</option>
                            <option value="One Size">One Size</option>
                        </select>
                        </div>
                        {formData.variants.length > 1 && (
                          <div className="mt-3 flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleRemoveVariant(index)}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Remove Variant
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Design Image Upload */}
                <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Design
                </label>
                {formData.designImage ? (
                    <div className="relative">
                    <img 
                        src={formData.designImage} 
                        alt="Product design" 
                        className="h-64 w-64 object-contain border rounded-md"
                    />
                    <button
                        type="button"
                        onClick={() => setFormData({...formData, designImage: null, designFile: null})}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                        </svg>
                        <p className="mb-2 text-sm text-gray-500">Upload design image</p>
                        </div>
                        <input 
                        id="design-upload" 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleDesignImageChange}
                        />
                    </label>
                    </div>
                )}
                </div>

                {/* Additional Images */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {formData.images.map((image) => (
                      <div key={image._id} className="relative">
                        <img 
                          src={image.url} 
                          alt={`Product ${image._id}`}
                          className="h-32 w-full object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(image._id)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    {formData.newImages.map((file, index) => (
                      <div key={`new-${index}`} className="relative">
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={`New image ${index}`}
                          className="h-32 w-full object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(null, index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                        </svg>
                        <p className="mb-2 text-sm text-gray-500">Upload additional images</p>
                      </div>
                      <input 
                        id="additional-images-upload" 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        multiple
                        onChange={handleAdditionalImagesChange}
                      />
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Product'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        ) : (
          // Products List
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Your Products</h2>
              <button
                onClick={handleCreateProduct}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                Add New Product
              </button>
            </div>
            <div className="divide-y divide-gray-200">
              {products.length === 0 ? (
                <div className="px-6 py-4 text-center">
                  <p className="text-gray-500 mb-4">No products found. Create your first product to get started.</p>
                  <button
                    onClick={handleCreateProduct}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                  >
                    Add Your First Product
                  </button>
                </div>
              ) : (
                products.map(product => (
                  <div key={product._id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 h-16 w-16 rounded-md overflow-hidden">
                        {product.design?.thumbnail ? (
                          <img 
                            src={product.design.thumbnail} 
                            alt={product.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xs text-gray-500">No Image</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-500 capitalize">{product.category}</p>
                        <p className="text-sm text-gray-500">
                          ₹{product.variants?.[0]?.finalPrice || product.variants?.[0]?.price || '0'} - {product.variants?.length || 0} variants
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product._id)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}